import { useCallback, useEffect, useRef, useState } from "react";
import useScrollLoader from "./useScrollLoader";

/**
 * Custom hook for handling infinite scroll functionality
 * @param {Object} options - Configuration options
 * @param {Function} options.fetchMore - Function to call when more data should be fetched
 * @param {number} options.initialPage - Initial page number (default: 1)
 * @param {number} options.maxPage - Maximum number of pages available
 * @param {boolean} options.isLoading - Loading state from the parent component
 * @param {number} options.threshold - Distance from bottom to trigger fetch (default: 20)
 * @returns {Object} - Returns ref and handler for the scroll container
 */
const useInfiniteScroll = ({
  fetchMore,
  initialPage = 1,
  maxPage = 1,
  isLoading = false,
  threshold = 20,
  showLoader = true,
  CustomLoader,
}) => {
  const [activePage, setActivePage] = useState(initialPage);
  const isFetching = useRef(false);
  const [containerRef, setContainerRef] = useState(null);
  const { mountLoader, unmountLoader } = useScrollLoader(CustomLoader);

  const handleScroll = useCallback(() => {
    if (
      !containerRef ||
      isLoading ||
      activePage >= maxPage ||
      isFetching.current
    )
      return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - threshold;
    console.log(isNearBottom);
    if (isNearBottom) {
      console.log("Fetching more data...");
      const nextPage = activePage + 1;
      isFetching.current = true;
      if (showLoader) mountLoader(containerRef);
      fetchMore(nextPage).then(() => {
        isFetching.current = false;
        if (showLoader) unmountLoader();
      });
      setActivePage(nextPage);
    }
  }, [
    activePage,
    fetchMore,
    isLoading,
    maxPage,
    threshold,
    containerRef,
    mountLoader,
    unmountLoader,
    showLoader,
  ]);

  useEffect(() => {
    console.log(">>>>>>>>>>>", containerRef);
    if (!containerRef) return;

    containerRef.addEventListener("scroll", handleScroll);

    return () => {
      containerRef.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef, handleScroll]);

  const resetPagination = useCallback(() => {
    setActivePage(initialPage);
  }, [initialPage]);

  return {
    containerRef,
    setContainerRef,
    handleScroll,
    activePage,
    setActivePage,
    resetPagination,
  };
};

export default useInfiniteScroll;
